import React from 'react';
import './FlowOverview.css';

import { Tooltip } from 'antd';

const FlowOverview = ({ onNodeClick, selectedNode, followUpStates, remEnabled }) => {
    // Define node positions and connections
    const nodes = [
        // Main lane (y=60)
        { id: 'invite', abbr: 'Inv', fullText: 'Invite', x: 30, y: 60, clickable: true },
        
        // Follow-up branch (below Invite, y=100)
        { id: 'fu1', abbr: 'FU1', fullText: 'Follow-up 1', x: 75, y: 100, clickable: true },
        { id: 'fu2', abbr: 'FU2', fullText: 'Follow-up 2', x: 120, y: 100, clickable: true },
        { id: 'fu3', abbr: 'FU3', fullText: 'Follow-up 3', x: 165, y: 100, clickable: true },
        
        { id: 'hasslot', abbr: 'Slot', fullText: 'Has Slot?', x: 210, y: 60, clickable: false },
        
        // No availability branch (below Has Slot, y=100)
        { id: 'noneavail', abbr: 'None', fullText: 'None Available', x: 255, y: 100, clickable: true },
        
        { id: 'presentsched', abbr: 'Pres', fullText: 'Present Schedule', x: 280, y: 60, clickable: false },
        { id: 'booked', abbr: 'Book', fullText: 'Booked', x: 350, y: 60, clickable: false },
        { id: 'confirmed', abbr: 'Conf', fullText: 'Confirmed', x: 420, y: 60, clickable: true },
        { id: 'reminder', abbr: 'Rem', fullText: 'Reminder', x: 490, y: 60, clickable: true },
        { id: 'event', abbr: 'Evt', fullText: 'Event', x: 560, y: 60, clickable: false },

        // Cancel branch (below Reminder, y=100): "3" from the booking confirmation
        // or the reminder → YES/NO guard → Cancelled message (ADR-038)
        { id: 'cancelled', abbr: 'Canc', fullText: 'Cancelled', x: 525, y: 100, clickable: true },
    ];

    const connections = [
        // Main flow
        { from: 'invite', to: 'hasslot', type: 'straight' },
        { from: 'hasslot', to: 'presentsched', type: 'straight' },
        { from: 'presentsched', to: 'booked', type: 'straight' },
        { from: 'booked', to: 'confirmed', type: 'straight' },
        { from: 'confirmed', to: 'reminder', type: 'straight' },
        { from: 'reminder', to: 'event', type: 'straight' },
        
        // Follow-up branch (Dashed)
        { from: 'invite', to: 'fu1', type: 'branch-down', dashed: true },
        { from: 'fu1', to: 'fu2', type: 'straight', dashed: true },
        { from: 'fu2', to: 'fu3', type: 'straight', dashed: true },
        { from: 'fu3', to: 'hasslot', type: 'branch-up', dashed: true },
        
        // No availability branch
        { from: 'hasslot', to: 'noneavail', type: 'branch-down' },

        // Cancel branch (candidate replies "3" after booking or from the reminder)
        { from: 'booked', to: 'cancelled', type: 'branch-down', dashed: true },
        { from: 'reminder', to: 'cancelled', type: 'branch-down', dashed: true },
    ];

    const getNodeById = (id) => nodes.find(n => n.id === id);

    const handleNodeClick = (node) => {
        if (node.clickable && onNodeClick) {
            onNodeClick(node.id);
        }
    };

    const renderConnection = (conn, index) => {
        const fromNode = getNodeById(conn.from);
        const toNode = getNodeById(conn.to);
        
        if (!fromNode || !toNode) return null;

        const nodeRadius = 14; // Smaller radius

        const style = conn.dashed ? { strokeDasharray: "4,4" } : {};

        if (conn.type === 'straight') {
            return (
                <line
                    key={`conn-${index}`}
                    x1={fromNode.x + nodeRadius}
                    y1={fromNode.y}
                    x2={toNode.x - nodeRadius}
                    y2={toNode.y}
                    className="flow-connection"
                    style={style}
                />
            );
        } else if (conn.type === 'branch-down') {
            const midX = fromNode.x + nodeRadius;
            const controlX = midX + 15;
            return (
                <path
                    key={`conn-${index}`}
                    d={`M ${fromNode.x + nodeRadius} ${fromNode.y} 
                        C ${controlX} ${fromNode.y}, ${controlX} ${toNode.y}, ${toNode.x - nodeRadius} ${toNode.y}`}
                    className="flow-connection"
                    fill="none"
                    style={style}
                />
            );
        } else if (conn.type === 'branch-up') {
            const controlX = fromNode.x + nodeRadius + 15;
            return (
                <path
                    key={`conn-${index}`}
                    d={`M ${fromNode.x + nodeRadius} ${fromNode.y} 
                        C ${controlX} ${fromNode.y}, ${controlX} ${toNode.y}, ${toNode.x - nodeRadius} ${toNode.y}`}
                    className="flow-connection"
                    fill="none"
                    style={style}
                />
            );
        }
    };

    const renderNode = (node) => {
        const nodeRadius = 14; // Smaller radius
        const isSelected = selectedNode === node.id || 
                          (selectedNode && selectedNode.startsWith('fu') && node.id.startsWith('fu')); // Group styling for FUs
        
        let isDisabledFeature = false;
        const fuIndex = { fu1: 0, fu2: 1, fu3: 2 };
        if (node.id in fuIndex && followUpStates && !followUpStates[fuIndex[node.id]]) {
            isDisabledFeature = true;
        }
        if (node.id === 'reminder' && !remEnabled) isDisabledFeature = true;

        return (
            <Tooltip title={node.fullText} key={node.id}>
                <g 
                    className={`flow-node ${node.clickable ? 'clickable' : 'non-clickable'} ${isSelected ? 'selected' : ''} ${isDisabledFeature ? 'feature-disabled' : ''}`}
                    onClick={() => handleNodeClick(node)}
                >
                    <circle
                        cx={node.x}
                        cy={node.y}
                        r={nodeRadius}
                        className="flow-node-circle"
                    />
                    <text
                        x={node.x}
                        y={node.y + 1}
                        className="flow-node-label"
                        textAnchor="middle"
                        dominantBaseline="middle"
                    >
                        {node.abbr}
                    </text>
                </g>
            </Tooltip>
        );
    };

    return (
        <div className="flow-overview-card">
            <div className="flow-overview-header">
                <span className="flow-overview-title">Flow Overview</span>
                <div className="flow-overview-legend">
                    <span className="legend-dot"></span>
                    <span className="legend-text">Editable Message Templates</span>
                </div>
            </div>
            <div className="flow-diagram-container">
                <svg className="flow-diagram" viewBox="0 0 600 130" preserveAspectRatio="xMidYMid meet">
                    {/* Render connections first */}
                    {connections.map((conn, index) => renderConnection(conn, index))}
                    
                    {/* Render nodes */}
                    {nodes.map(node => renderNode(node))}
                </svg>
            </div>
        </div>
    );
};

export default FlowOverview;
